import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import { TmsMessageAction, TmsNotificationService } from './tms-notification.service';
import { toTsmMode } from '../components/tsm-app/models/tsm-app.model';
import { NavFrame } from '../store/nav-stack/tms-nav-stack.model';
import { TmsNavStackState } from '../store/nav-stack/tms-nav-stack.state';
import { normalizeNewlines } from '../utils/normalize-newlines';
import { SaveResponseMessage, saveErrorText } from '../components/tsm-app/utils/mandatory-field-errors.util';
import { shouldAnnounceSuccess } from './tms-message-policy';

// Every TMS app (TSM / REF / Filter / Report) saves through the one shared
// AppData endpoint, so its responses are the single uniform place to capture
// user messages from — independent of which app or client path triggered them.
const APP_DATA_PATH: string = '/api/v1/app-data';

// A forced matrix repopulate (?action=load&repopulate=true) IS a user Search —
// it runs the app's query and returns the app's own outcome ("No payments
// available for re-release."). It rides the MATRIX endpoint, so capturing only
// APP_DATA_PATH silently dropped those messages from the history while the
// toast still showed: the popover disagreed with the screen. Captured here, at
// the one capture point, rather than making the call site push (which would
// double-log every app-data message).
const MATRIX_PATH: string = '/api/v1/matrix';
const REPOPULATE_FLAG: string = 'repopulate=true';

// A FIELD-UPDATE (?action=update with a `fields` delta body) is an incremental
// field SYNC fired on every keystroke / lookup-pick — NOT a user action. Its
// response echoes a stale, re-defaulted footer STATUS_MESSAGE ("Welcome to <App>",
// built at runtime from the app title) that must not enter the history, else a
// filter-panel lookup re-logs the welcome on every pick. This is distinct from —
// and intentionally does NOT touch — the paths that carry genuine outcomes:
// save (?action=save), flag-bump / button cascades (?action=refresh +
// `changedFields`), and bootstrap (?action=update WITHOUT `fields`, which may
// surface the welcome once on first load). errorMessage is logged on any path.
const UPDATE_ACTION: string = 'action=update';

// The message-bearing fields of an AppData response (see DeltaResponse):
// `statusMessage` (save confirmation; or the transient footer status echoed on
// reads/field-updates), `errorMessage` (validation/business errors — any path).
interface AppDataMessageEnvelope {
	readonly statusMessage?: string;
	readonly errorMessage?: string;
	// A save's per-field details ("<Label> is required"), named in the recorded error.
	readonly messages?: ReadonlyArray<SaveResponseMessage>;
	readonly formId?: string;
	// Post-action results matrix (server-drained legacy popup directive).
	// When present, the history entry carries a "View results" action so the
	// closed sheet stays reachable (user decision, results-matrix-popup.md §6).
	readonly resultsMatrix?: string;
	readonly resultsMatrixTitle?: string;
	readonly resultsMatrixApp?: string;
	readonly resultsMatrixMode?: string;
	readonly resultsMatrixRunId?: string;
}

// Request body shape we discriminate on — a field-update carries a `fields` delta.
interface AppDataRequestBody {
	readonly fields?: unknown;
}

/**
 * Message-system SSoT, capture side — the one uniform rule for the "message
 * trip" across every TMS app type. Watches the shared {@link APP_DATA_PATH}
 * responses and records the server's `statusMessage` (success) / `errorMessage`
 * (error) into {@link TmsNotificationService} so the shared popover lists them.
 *
 * <p>Records history only — the transient green/red toasts stay owned by the
 * individual call sites (each app shows its own bubble). This interceptor is
 * the single client touch-point that makes every app populate the same history
 * with no per-site wiring.
 */
@Injectable()
export class TmsMessageInterceptor implements HttpInterceptor {
	private readonly notify: TmsNotificationService = inject(TmsNotificationService);
	private readonly store: Store = inject(Store);

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		// `req.url` is the path ALONE — Angular keeps query params in `req.params`
		// until the request is serialized, so matching a `key=value` flag against it
		// silently never fires. `urlWithParams` is the serialized URL and is what
		// every query-string test here must read.
		const url: string = req.urlWithParams;
		const isForcedRepopulate: boolean = url.includes(MATRIX_PATH) && url.includes(REPOPULATE_FLAG);
		if (!url.includes(APP_DATA_PATH) && !isForcedRepopulate) {
			return next.handle(req);
		}
		return next.handle(req).pipe(
			tap((event: HttpEvent<unknown>): void => {
				if (!(event instanceof HttpResponse)) return;
				const body: AppDataMessageEnvelope | null = event.body as AppDataMessageEnvelope | null;
				if (!body) return;
				const app: string | null = this.currentApp();
				// errorMessage and statusMessage are mutually exclusive (error vs
				// success); check error first so a malformed both-present payload
				// surfaces as the error it is.
				const action: TmsMessageAction | undefined = this.resultsAction(body);
				if (body.errorMessage) {
					this.notify.record('error', normalizeNewlines(saveErrorText(body)), { app }, action);
				} else if (body.statusMessage && shouldAnnounceSuccess({ quiet: this.isQuiet(req), fieldUpdate: this.isFieldUpdate(req) })) {
					this.notify.record('success', normalizeNewlines(body.statusMessage), { app }, action);
				}
			}),
		);
	}

	/**
	 * Reopen affordance for a post-action results matrix. The response is a
	 * self-contained descriptor (matrix/app/mode/formId all server-emitted),
	 * so this capture point stays app-agnostic. Serializable by design — the
	 * popover renders it as a "View results" button and opens the shared
	 * matrix sheet; the sheet load routes to the response's formId, where the
	 * results rows live until the next action on that form.
	 */
	private resultsAction(body: AppDataMessageEnvelope): TmsMessageAction | undefined {
		// formId is as load-bearing as the matrix identity: the results rows
		// live on that cached form, and a formId-less load "succeeds" with an
		// empty grid — an affordance that can only ever show nothing.
		if (!body.resultsMatrix || !body.resultsMatrixApp || !body.formId) {
			return undefined;
		}
		return {
			kind: 'open-matrix-sheet',
			label: 'View results',
			matrixName: body.resultsMatrix,
			app: body.resultsMatrixApp,
			mode: toTsmMode(body.resultsMatrixMode),
			formId: body.formId,
			title: body.resultsMatrixTitle ?? null,
			runId: body.resultsMatrixRunId ?? null,
		};
	}

	/**
	 * True when this request is a field-update sync (?action=update carrying a
	 * `fields` delta), as opposed to a save / flag-bump / bootstrap. Field-updates
	 * re-emit the app's transient footer STATUS_MESSAGE (the welcome), which is not
	 * a user-action outcome and must stay out of the history.
	 */
	private isFieldUpdate(req: HttpRequest<unknown>): boolean {
		// urlWithParams, not url — see intercept().
		if (!req.urlWithParams.includes(UPDATE_ACTION)) {
			return false;
		}
		const body: AppDataRequestBody | null = req.body as AppDataRequestBody | null;
		return body != null && typeof body === 'object' && body.fields != null;
	}

	/**
	 * True when the request carries the client-side {@code quiet} marker — the
	 * post-save record re-read. Legacy never re-reads after a save, so its
	 * message stream shows only the save confirmation; the quiet flag keeps our
	 * re-read's "Found the Record!" out of the history the same way. The server
	 * ignores the extra body field; errors are still recorded on any path.
	 */
	private isQuiet(req: HttpRequest<unknown>): boolean {
		const body: AppDataRequestBody | null = req.body as AppDataRequestBody | null;
		return body != null && typeof body === 'object' && (body as { quiet?: boolean }).quiet === true;
	}

	/**
	 * The currently-open app's display name for message provenance. Uses the
	 * breadcrumb's current frame `label` — the friendly, server-resolved,
	 * language-dependent title (e.g. "BAI Type Code", "Short Term Debt Trade") —
	 * falling back to the technical app code, then null when off a TMS app.
	 */
	private currentApp(): string | null {
		const frame: NavFrame | null = this.store.selectSnapshot(TmsNavStackState.currentFrame);
		// During the seed→resolve window the frame's label is the appCd placeholder
		// (labelPending) — don't tag the message with the raw technical code; leave it
		// null until the friendly title resolves (mirrors the breadcrumb's gate).
		if (frame?.labelPending) {
			return null;
		}
		return frame?.label || frame?.appCd || null;
	}
}
